package fr.manyverse.app.presentation.components.gm_screen

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.BarChart
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Stop
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SecondaryTabRow
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.SoundType
import fr.manyverse.app.infrastructure.audiopicker.rememberAudioPickerLauncher
import fr.manyverse.app.presentation.components.soundboard.AddSoundDialog
import fr.manyverse.app.presentation.components.soundboard.NowPlayingOverlay
import fr.manyverse.app.presentation.components.soundboard.RadialMusicSelector
import fr.manyverse.app.presentation.components.soundboard.SoundFilterChips
import fr.manyverse.app.presentation.components.soundboard.SoundListItem
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.SoundPlayerViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.add_sound
import manyverse.shared.generated.resources.kill_all
import manyverse.shared.generated.resources.master_audio_console
import manyverse.shared.generated.resources.search_sounds
import manyverse.shared.generated.resources.type_ambient
import manyverse.shared.generated.resources.type_effect
import manyverse.shared.generated.resources.type_music
import org.jetbrains.compose.resources.stringResource

private val soundTypeTabs = listOf(SoundType.MUSIC, SoundType.AMBIENT, SoundType.EFFECT)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SoundsTab(
    viewModel: SoundPlayerViewModel,
    modifier: Modifier = Modifier
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val currentSoundId = uiState.playbackState.currentSoundId
    val currentSound = uiState.currentSound
    val musicCurrentSound = currentSound?.takeIf { it.type == SoundType.MUSIC }

    val listState = rememberLazyListState()

    LaunchedEffect(Unit) {
        viewModel.randomSoundPlayed.collect { soundId ->
            val currentSounds = viewModel.uiState.value.sounds
            val index = currentSounds.indexOfFirst { it.id == soundId }
            if (index >= 0) {
                listState.animateScrollToItem(index)
            }
        }
    }

    val launchAudioPicker = rememberAudioPickerLauncher { audioData ->
        viewModel.selectAudioData(audioData)
    }

    val selectedTabIndex = soundTypeTabs.indexOfFirst {
        it in uiState.selectedTypes
    }.coerceAtLeast(0)

    Box(modifier = modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize()) {

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Row(
                    modifier = Modifier
                        .weight(1f)
                        .background(
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                            RoundedCornerShape(12.dp)
                        )
                        .padding(horizontal = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.BarChart,
                        contentDescription = stringResource(Res.string.master_audio_console),
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(18.dp)
                    )
                    val thumbColor = MaterialTheme.colorScheme.primary
                    Slider(
                        value = uiState.masterVolume,
                        onValueChange = viewModel::setMasterVolume,
                        modifier = Modifier.weight(1f),
                        thumb = {
                            Box(
                                modifier = Modifier
                                    .size(14.dp)
                                    .background(thumbColor, CircleShape)
                            )
                        },
                        track = { sliderState ->
                            val active = thumbColor
                            val inactive = thumbColor.copy(alpha = 0.3f)
                            Canvas(modifier = Modifier.fillMaxWidth()) {
                                val y = size.height / 2
                                val stroke = 2.dp.toPx()
                                val end = size.width * sliderState.value.coerceIn(0f, 1f)
                                drawLine(
                                    inactive,
                                    Offset(0f, y),
                                    Offset(size.width, y),
                                    stroke,
                                    StrokeCap.Round
                                )
                                drawLine(
                                    active,
                                    Offset(0f, y),
                                    Offset(end, y),
                                    stroke,
                                    StrokeCap.Round
                                )
                            }
                        },
                        colors = SliderDefaults.colors(
                            activeTrackColor = thumbColor,
                            inactiveTrackColor = thumbColor.copy(alpha = 0.3f)
                        )
                    )
                }
                IconButton(
                    onClick = viewModel::stopAll,
                    modifier = Modifier
                        .size(36.dp)
                        .background(Color(0xFF3D0A1A), RoundedCornerShape(8.dp))
                ) {
                    Icon(
                        imageVector = Icons.Default.Stop,
                        contentDescription = stringResource(Res.string.kill_all),
                        tint = Color(0xFFFF3D6B),
                        modifier = Modifier.size(18.dp)
                    )
                }
            }

            // Type tabs
            SecondaryTabRow(
                selectedTabIndex = selectedTabIndex,
                containerColor = Color.Transparent,
                modifier = Modifier.fillMaxWidth()
            ) {
                soundTypeTabs.forEachIndexed { index, type ->
                    Tab(
                        selected = index == selectedTabIndex,
                        onClick = { viewModel.selectTypeTab(type) },
                        text = {
                            Text(
                                text = when (type) {
                                    SoundType.MUSIC -> stringResource(Res.string.type_music)
                                    SoundType.AMBIENT -> stringResource(Res.string.type_ambient)
                                    SoundType.EFFECT -> stringResource(Res.string.type_effect)
                                }.uppercase(),
                                style = MaterialTheme.typography.labelMedium,
                                letterSpacing = 1.sp
                            )
                        }
                    )
                }
            }

            // Tag filter chips
            SoundFilterChips(
                selectedTags = uiState.selectedTags,
                onTagToggle = viewModel::toggleTagFilter,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 8.dp)
            )

            // Add button + Search field
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 16.dp, end = 16.dp, top = 0.dp, bottom = 12.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FilledIconButton(
                    onClick = viewModel::showAddDialog,
                    colors = IconButtonDefaults.filledIconButtonColors(MaterialTheme.colorScheme.primaryContainer)
                ) {
                    Icon(
                        imageVector = Icons.Default.Add,
                        contentDescription = stringResource(Res.string.add_sound),
                        tint = MaterialTheme.colorScheme.onPrimaryContainer
                    )
                }

                // Quick music change button (radial selector)
                RadialMusicSelector(
                    onTagSelected = viewModel::selectAutoPlayTag,
                    activeTag = uiState.autoPlayTag,
                    onDeactivate = viewModel::deactivateAutoPlay
                )

                OutlinedTextField(
                    value = uiState.searchQuery,
                    onValueChange = viewModel::updateSearchQuery,
                    placeholder = { Text(stringResource(Res.string.search_sounds)) },
                    leadingIcon = {
                        Icon(Icons.Default.Search, contentDescription = null)
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(24.dp),
                    modifier = Modifier.weight(1f)
                )
            }

            // Sound list
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
                contentPadding = PaddingValues(
                    bottom = if (musicCurrentSound != null) 80.dp else 16.dp
                )
            ) {
                items(
                    items = uiState.sounds,
                    key = { it.id }
                ) { sound ->
                    val isCurrentSound = sound.id == currentSoundId

                    SoundListItem(
                        sound = sound,
                        isPlaying = isCurrentSound && uiState.playbackState.isPlaying,
                        isCrossfading = isCurrentSound && uiState.playbackState.isCrossfading,
                        volume = viewModel.getSoundVolume(sound.id),
                        onPlayClick = {
                            if (sound.type == SoundType.AMBIENT) {
                                viewModel.playSound(sound)
                            } else if (isCurrentSound) {
                                viewModel.togglePlayPause()
                            } else {
                                viewModel.playSound(sound)
                            }
                        },
                        onVolumeChange = { volume ->
                            viewModel.setSoundVolume(sound.id, volume)
                        },
                        onDeleteClick = if (viewModel.canDeleteSound(sound)) {
                            { viewModel.deleteSound(sound.id) }
                        } else null,
                        isAmbientActive = sound.type == SoundType.AMBIENT &&
                                sound.id in uiState.activeAmbientSounds
                    )
                }
            }
        }

        // Now playing overlay at bottom - only show for MUSIC type
        NowPlayingOverlay(
            currentSound = musicCurrentSound,
            isPlaying = uiState.playbackState.isPlaying,
            isCrossfading = uiState.playbackState.isCrossfading,
            progress = uiState.playbackState.progress,
            onPlayPauseClick = viewModel::togglePlayPause,
            onStopClick = viewModel::stopSound,
            showNextTrackButton = uiState.autoPlayTag != null,
            onNextTrackClick = viewModel::playNextTrack,
            modifier = Modifier.align(Alignment.BottomCenter)
        )

        // Add sound dialog
        AddSoundDialog(
            showDialog = uiState.showAddDialog,
            selectedAudioData = uiState.selectedAudioData,
            onPickAudio = launchAudioPicker,
            onDismiss = viewModel::hideAddDialog,
            onConfirm = viewModel::uploadSound,
            isLoading = uiState.uploadState is AsyncUiState.Loading,
            errorMessage = (uiState.uploadState as? AsyncUiState.Error)?.message
        )
    }
}
